import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { SITE_ID } from '@/lib/constants';
import { useAuth } from '@/contexts/AuthContext';

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: {
    urun_id?: string;
    urun_adi: string;
    gorsel?: string;
  };
  onSubmitted: () => void;
}

export default function ReviewModal({ isOpen, onClose, product, onSubmitted }: ReviewModalProps) {
  const { user } = useAuth();
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen || !product.urun_id) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (comment.length > 1000) {
      setError('Yorum en fazla 1000 karakter olabilir.');
      return;
    }

    setSubmitting(true);
    setError(null);

    const { error: insertErr } = await supabase.from('urun_yorumlari').insert({
      site_id: SITE_ID,
      urun_id: product.urun_id,
      kullanici_id: user?.id || null,
      musteri_adi: user?.user_metadata?.full_name || user?.email || 'Misafir',
      musteri_email: user?.email || null,
      puan: rating,
      yorum_baslik: null,
      yorum_metni: comment.trim() || null,
      onaylandi: false,
    });

    setSubmitting(false);

    if (insertErr) {
      setError('Yorum gönderilemedi, lütfen tekrar deneyin.');
    } else {
      setSubmitted(true);
      setTimeout(() => {
        onSubmitted();
        onClose();
        setSubmitted(false);
        setRating(5);
        setComment('');
      }, 1500);
    }
  };

  const renderStars = (count: number, interactive = false) => (
    <div className="flex items-center gap-1">
      {Array.from({ length: 5 }, (_, i) => (
        <button
          key={i}
          type="button"
          onClick={() => interactive && setRating(i + 1)}
          className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
            interactive ? 'hover:bg-gold/10 cursor-pointer' : ''
          }`}
          disabled={!interactive}
        >
          <i className={`text-xl ${i < count ? 'ri-star-fill text-gold' : 'ri-star-line text-cream-dark'}`} />
        </button>
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-cream-dark">
          <h3 className="font-serif text-lg font-semibold text-coffee">Ürünü Değerlendir</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-cream transition-colors cursor-pointer">
            <i className="ri-close-line text-coffee" />
          </button>
        </div>

        {/* Product Info */}
        <div className="px-6 py-4 bg-cream/30 flex items-center gap-3">
          {product.gorsel ? (
            <img src={product.gorsel} alt={product.urun_adi} className="w-14 h-14 rounded-xl object-cover object-top flex-shrink-0" />
          ) : (
            <div className="w-14 h-14 rounded-xl bg-cream flex items-center justify-center flex-shrink-0">
              <i className="ri-cup-line text-coffee-light text-xl" />
            </div>
          )}
          <p className="text-sm font-medium text-coffee line-clamp-2">{product.urun_adi}</p>
        </div>

        {/* Form */}
        <div className="p-6">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                <i className="ri-check-line text-green-600 text-2xl" />
              </div>
              <h4 className="font-serif text-lg font-semibold text-green-700 mb-2">Yorumunuz Alındı!</h4>
              <p className="text-sm text-coffee-light">Onaylandıktan sonra ürün sayfasında yayınlanacak.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Rating */}
              <div>
                <label className="block text-sm font-medium text-coffee mb-2">Puanınız</label>
                {renderStars(rating, true)}
                <p className="text-xs text-coffee-light mt-1">
                  {rating === 5 && 'Mükemmel!'}
                  {rating === 4 && 'Çok İyi'}
                  {rating === 3 && 'İyi'}
                  {rating === 2 && 'Kötü'}
                  {rating === 1 && 'Çok Kötü'}
                </p>
              </div>

              {/* Comment */}
              <div>
                <label className="block text-sm font-medium text-coffee mb-1.5">
                  Yorumunuz <span className="text-coffee-light font-normal">(isteğe bağlı)</span>
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Ürün hakkında düşüncelerinizi paylaşın..."
                  rows={4}
                  maxLength={1000}
                  className="w-full px-4 py-3 text-sm bg-white border border-cream-dark rounded-xl focus:outline-none focus:border-gold text-coffee placeholder:text-coffee-light/50 transition-colors resize-none"
                />
                <p className="text-xs text-coffee-light mt-1 text-right">{comment.length}/1000</p>
              </div>

              {error && (
                <div className="flex items-center gap-2 text-sm text-red-500 bg-red-50 px-4 py-2.5 rounded-xl border border-red-100">
                  <i className="ri-error-warning-line text-base" />
                  {error}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2.5 border border-cream-dark text-coffee-medium text-sm font-medium rounded-xl hover:bg-cream transition-colors cursor-pointer whitespace-nowrap"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2.5 bg-coffee text-white text-sm font-semibold rounded-xl hover:bg-coffee-medium transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-1" />
                      Gönderiliyor...
                    </>
                  ) : (
                    'Yorumu Gönder'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}